// src/Formatter/utils.ts
var intlFormatter = new Intl.NumberFormat("en");
function formatNumber(value) {
  if (Number.isNaN(value)) {
    return `${value}`;
  }
  return intlFormatter.format(value).replace(/,/g, "\xA0");
}
function formatNumberTruncated(value, significativeNumbers = 3, truncate = 3) {
  if (value === 0 || value === 0) {
    return `0`;
  }
  const power = significativeNumbers - Math.ceil(Math.log(Math.abs(value)) / Math.LN10);
  const magnitude = 10 ** power;
  const shifted = Math.round(magnitude * value);
  const result = shifted / magnitude;
  if (Math.floor(Math.abs(result)).toString().length >= significativeNumbers) {
    return formatNumber(Math.round(result));
  }
  return formatNumber(Math.round(result * 10 ** truncate) / 10 ** truncate);
}

// src/Formatter/BaseFormatter.ts
var BaseFormatter = class {
  toStandardString(value) {
    const { standardValue, standardUnit } = this.toStandardComponents(value);
    if (!standardUnit.length) {
      return standardValue;
    }
    return `${standardValue}${standardUnit}`;
  }
  toStandardComponents(value) {
    return {
      standardValue: formatNumber(value),
      standardUnit: ""
    };
  }
  toCondensedString(value) {
    const { condensedValue, condensedUnit } = this.toCondensedComponents(value);
    if (!condensedUnit.length) {
      return condensedValue;
    }
    return `${condensedValue}${condensedUnit}`;
  }
  toCondensedComponents(value) {
    const absValue = Math.abs(value);
    if (absValue >= 1e9) {
      return {
        condensedValue: formatNumberTruncated(value / 1e9, 2, 2),
        condensedUnit: "G"
      };
    }
    if (absValue >= 1e6) {
      return {
        condensedValue: formatNumberTruncated(value / 1e6, 2, 2),
        condensedUnit: "M"
      };
    }
    if (absValue >= 1e3) {
      return {
        condensedValue: formatNumberTruncated(value / 1e3, 2, 2),
        condensedUnit: "k"
      };
    }
    return {
      condensedValue: formatNumberTruncated(value, 2, 2),
      condensedUnit: ""
    };
  }
  toComponents(value) {
    return {
      ...this.toStandardComponents(value),
      ...this.toCondensedComponents(value)
    };
  }
};

// src/Formatter/ByteFormatter.ts
var ByteFormatter = class extends BaseFormatter {
  toStandardComponents(value) {
    return {
      standardValue: formatNumber(value),
      standardUnit: "B"
    };
  }
  toCondensedComponents(value) {
    const absValue = Math.abs(value);
    if (absValue >= 1024 * 1024 * 1024 / 2) {
      return {
        condensedValue: formatNumberTruncated(value / 1024 / 1024 / 1024, 2, 2),
        condensedUnit: "GiB"
      };
    }
    if (absValue >= 1024 * 1024 / 2) {
      return {
        condensedValue: formatNumberTruncated(value / 1024 / 1024, 2, 2),
        condensedUnit: "MiB"
      };
    }
    if (absValue >= 1024 / 2) {
      return {
        condensedValue: formatNumberTruncated(value / 1024, 2, 2),
        condensedUnit: "KiB"
      };
    }
    return {
      condensedValue: formatNumberTruncated(value, 2, 2),
      condensedUnit: "B"
    };
  }
};

// src/Formatter/DurationFormatter.ts
var DurationFormatter = class extends BaseFormatter {
  toStandardComponents(value) {
    return {
      standardValue: formatNumber(value),
      standardUnit: "\xB5s"
    };
  }
  toCondensedString(value) {
    const absValue = Math.abs(value);
    if (absValue < 6e7) {
      return super.toCondensedString(value);
    }
    const seconds = Math.round(absValue / 1e6 % 60);
    const minutes = Math.floor(absValue / 6e7 % 60);
    const hours = Math.floor(absValue / 36e8);
    const parts = [];
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}min`);
    }
    if (seconds > 0 && hours === 0) {
      parts.push(`${seconds}s`);
    }
    return `${value < 0 ? "-" : ""}${parts.join("\xA0")}`;
  }
  toCondensedComponents(value) {
    const absValue = Math.abs(value);
    if (absValue >= 36e8) {
      return {
        condensedValue: formatNumberTruncated(value / 36e8, 1, 1),
        condensedUnit: "h"
      };
    }
    if (absValue >= 6e7) {
      return {
        condensedValue: formatNumberTruncated(value / 6e7, 1, 1),
        condensedUnit: "min"
      };
    }
    if (absValue >= 1e6) {
      return {
        condensedValue: formatNumberTruncated(value / 1e6, 2, 2),
        condensedUnit: "s"
      };
    }
    if (absValue >= 1e3) {
      return {
        condensedValue: formatNumberTruncated(value / 1e3, 2, 2),
        condensedUnit: "ms"
      };
    }
    return {
      condensedValue: formatNumberTruncated(value),
      condensedUnit: "\xB5s"
    };
  }
};

// src/Formatter/PercentFormatter.ts
var PercentFormatter = class extends BaseFormatter {
  toStandardComponents(value) {
    return {
      standardValue: formatNumber(value * 100),
      standardUnit: "%"
    };
  }
  toCondensedComponents(value) {
    return {
      condensedValue: formatNumberTruncated(value * 100, 2, 2),
      condensedUnit: "%"
    };
  }
};

// src/Formatter/ScaleFormatter.ts
var ScaleFormatter = class extends BaseFormatter {
  multiplier;
  decorated;
  constructor(decorated, multiplier) {
    super();
    this.decorated = decorated;
    this.multiplier = multiplier;
  }
  toStandardString(value) {
    return this.decorated.toStandardString(value * this.multiplier);
  }
  toStandardComponents(value) {
    return this.decorated.toStandardComponents(value * this.multiplier);
  }
  toCondensedString(value) {
    return this.decorated.toCondensedString(value * this.multiplier);
  }
  toCondensedComponents(value) {
    return this.decorated.toCondensedComponents(value * this.multiplier);
  }
  toComponents(value) {
    return this.decorated.toComponents(value * this.multiplier);
  }
};

// src/Formatter/SuffixFormatter.ts
var SuffixFormatter = class extends BaseFormatter {
  suffix;
  pluralize;
  decorated;
  constructor(decorated, suffix, pluralize = false) {
    super();
    this.decorated = decorated;
    this.suffix = suffix;
    this.pluralize = pluralize;
  }
  toStandardComponents(value) {
    const {
      standardValue,
      standardUnit
    } = this.decorated.toStandardComponents(value);
    const suffix = `${this.suffix}${this.pluralize && Math.abs(value) !== 1 ? "s" : ""}`;
    return {
      standardValue,
      standardUnit: `${standardUnit}${suffix}`
    };
  }
  toCondensedComponents(value) {
    const {
      condensedValue,
      condensedUnit
    } = this.decorated.toCondensedComponents(value);
    const suffix = `${this.suffix}${this.pluralize && Math.abs(value) !== 1 ? "s" : ""}`;
    return {
      condensedValue,
      condensedUnit: `${condensedUnit}${suffix}`
    };
  }
};
export {
  BaseFormatter,
  ByteFormatter,
  DurationFormatter,
  PercentFormatter,
  ScaleFormatter,
  SuffixFormatter
};
